/**
 * Type definitions and validation for deal inputs/outputs
 * Provides schema validation and default values
 */

/**
 * Default input values
 */
export const DEFAULT_INPUTS = {
  landPrice: 0,
  landSize: 0,
  buildabilityCoefficient: 1.0,
  netSaleablePercent: 0.80,
  pricePerSqm: 0,
  apartmentPricePerSqm: 0,
  housePricePerSqm: 0,
  apartmentSaleableShare: 0.5,
  parkingPrice: 0,
  numberOfParkingSpots: 0,
  numberOfUnits: 0,
  feasibilityStudy: 0,
  acomptePercent: 0.20,
  financingPercent: 0.70,
  feesPercent: 0.10,
};

/**
 * Input field configuration — used for rendering and validation guardrails
 */
export const INPUT_CONFIG = {
  landPrice: {
    label: 'Land Price',
    unit: 'CHF',
    min: 0,
    max: 100000000,
    step: 100000,
  },
  landSize: {
    label: 'Land Size',
    unit: 'm²',
    min: 0,
    max: 100000,
    step: 100,
  },
  buildabilityCoefficient: {
    label: 'Buildability Coefficient',
    unit: '',
    min: 0,
    max: 5.0,
    step: 0.01,
  },
  netSaleablePercent: {
    label: 'Net Saleable %',
    unit: '%',
    min: 0,
    max: 1,
    step: 0.01,
    isPercent: true,
  },
  pricePerSqm: {
    label: 'Price per m²',
    // Unit label is display-only; InputPanel uses operatingCurrency at render time.
    unit: '/m²',
    // High enough for JMD (and similar) finished-residential /m²; still blocks trillion typos.
    // Backend remains authoritative for extreme market-ref flags.
    min: 0,
    max: 50_000_000,
    step: 500,
  },
  apartmentPricePerSqm: {
    label: 'Apartment /m²',
    unit: '/m²',
    min: 0,
    max: 50_000_000,
    step: 50,
  },
  housePricePerSqm: {
    label: 'House /m²',
    unit: '/m²',
    min: 0,
    max: 50_000_000,
    step: 50,
  },
  apartmentSaleableShare: {
    label: 'Apartment share of saleable',
    unit: '%',
    min: 0,
    max: 1,
    step: 0.05,
    isPercent: true,
  },
  parkingPrice: {
    label: 'Parking Price',
    unit: 'CHF',
    min: 0,
    max: 200000,
    step: 5000,
  },
  numberOfParkingSpots: {
    label: 'Parking Spots',
    unit: '',
    min: 0,
    max: 500,
    step: 1,
  },
  numberOfUnits: {
    label: 'Units',
    unit: '',
    min: 0,
    max: 500,
    step: 1,
  },
  feasibilityStudy: {
    label: 'Feasibility',
    unit: 'CHF',
    min: 0,
    max: 10000000,
    step: 1000,
  },
};

/**
 * Capital structure slider configuration
 */
export const CAPITAL_SLIDER_CONFIG = {
  acomptePercent: {
    label: 'Acompte (Equity)',
    color: '#32D74B',
  },
  financingPercent: {
    label: 'Financing',
    color: '#FF9F0A',
  },
  feesPercent: {
    label: 'Fees',
    color: '#8A8A8E',
  },
};

export function clampInputValue(key, value) {
  const config = INPUT_CONFIG[key];
  if (!config) return value;
  const n = Number(value);
  if (isNaN(n) || !isFinite(n)) return config.min;
  return Math.max(config.min, Math.min(config.max, n));
}

/**
 * Validate inputs and return sanitized values (preserves extra fields).
 * @param {Object} inputs - Raw inputs
 * @returns {Object} Sanitized inputs
 */
export function validateInputs(inputs) {
  if (!inputs || typeof inputs !== 'object') return { ...DEFAULT_INPUTS };
  const sanitized = { ...inputs };

  for (const key of Object.keys(INPUT_CONFIG)) {
    if (sanitized[key] !== undefined && sanitized[key] !== null) {
      sanitized[key] = clampInputValue(key, sanitized[key]);
    }
  }

  if (sanitized.acomptePercent !== undefined) {
    sanitized.acomptePercent = Math.max(0, Math.min(1, Number(sanitized.acomptePercent) || 0));
  }
  if (sanitized.financingPercent !== undefined) {
    sanitized.financingPercent = Math.max(0, Math.min(1, Number(sanitized.financingPercent) || 0));
  }
  if (sanitized.feesPercent !== undefined) {
    sanitized.feesPercent = Math.max(0, Math.min(1, Number(sanitized.feesPercent) || 0));
  }

  return sanitized;
}

/** Detect whether raw inputs needed clamping (invalid/extreme before sanitize). */
export function inputsNeededClamping(raw) {
  if (!raw || typeof raw !== 'object') return false;
  for (const [key, config] of Object.entries(INPUT_CONFIG)) {
    if (raw[key] === undefined || raw[key] === null) continue;
    const n = Number(raw[key]);
    if (isNaN(n) || !isFinite(n) || n < config.min || n > config.max) return true;
  }
  return false;
}
